import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import CtaBand from '../components/CtaBand';
import { useContent } from '../content/ContentContext';
import { useUi } from '../i18n/ui';
import { renderMultiline, renderInlineMd } from '../utils/text';

const CLUB_ABOUT_FALLBACK = `Il Moto Club InsolitaSardegna nasce con l'obiettivo di promuovere il motociclismo fuoristrada, la navigazione e la scoperta del territorio attraverso eventi, competizioni e attività formative.

Da anni organizziamo manifestazioni di livello nazionale e internazionale, come il Sandalion Rally, il Sardegna Rally Raid e importanti eventi validi per i Campionati Italiani ed Europei, offrendo ai piloti percorsi tecnici, sicurezza e un'organizzazione di alto livello.

Ma il nostro impegno va ben oltre le competizioni. Crediamo che la moto sia uno strumento per vivere il territorio, creare amicizie, trasmettere valori e condividere esperienze. Per questo organizziamo:
• Corsi di navigazione con Roadbook e GPS.
• Corsi di guida in fuoristrada per principianti ed esperti.
• Tour guidati alla scoperta della Sardegna.
• Allenamenti e stage dedicati ai Rally Raid e al Motorally.
• Eventi promozionali e manifestazioni dedicate agli appassionati delle due ruote.
• Attività sociali e iniziative di inclusione, come progetti di mototerapia e avvicinamento dei giovani al motociclismo.

Il Moto Club mette inoltre a disposizione la propria esperienza per assistere piloti e team nella preparazione delle competizioni, nella logistica, nella navigazione e nell'organizzazione degli eventi.

La nostra missione è valorizzare la Sardegna attraverso il motociclismo, promuovendo uno sport praticato nel rispetto dell'ambiente, delle persone e del territorio. Forza, lealtà e amore del territorio non sono soltanto il nostro motto: sono i valori che guidano ogni iniziativa del Moto Club InsolitaSardegna.

Che tu voglia imparare a navigare, prepararti per un Rally Raid o semplicemente vivere un'esperienza indimenticabile in Sardegna, il Moto Club InsolitaSardegna è il punto di partenza della tua prossima avventura.`;

const Storia: React.FC = () => {
  const { t, img } = useContent();
  const { tr } = useUi();
  const phone = t('contact.phone', '+393467814537');
  return (
    <>
      <PageHero image={img('storia.hero.image', '/img/insolita-sardegna-storia.jpg')} crumb={tr('page.storia.title')} title={tr('page.storia.title')} />

      <section className="sec">
        <div className="wrap">
          <div className="split">
            <div className="reveal">
              <span className="eyebrow">{t('storia.chisiamo.eyebrow', 'Chi siamo')}</span>
              <h2 className="h-sec" style={{ margin: '.35em 0 .6em' }}>{renderMultiline(t('storia.chisiamo.title', 'Una storia\ndi passione'))}</h2>
              <p className="lead" style={{ marginBottom: '1.2em' }}>{renderInlineMd(t('storia.p1', 'È ufficialmente partita la Campagna tesseramenti 2026. Unisciti alla famiglia **Insolita Sardegna** e assicurati sconti su tessere, licenze, ma soprattutto un modo nuovo di vivere la tua passione su due ruote.'))}</p>
              <p className="lead" style={{ marginBottom: '1.2em' }}>{renderInlineMd(t('storia.p2', "La nostra è una storia di passione per lo sport e di amore per il nostro territorio. Alla base di questo progetto vogliamo mettere l'incentivazione ad uno sport che a noi ha dato tanto, consentendoci di conoscere meglio il territorio che ci ospita."))}</p>
              <p className="lead">{renderInlineMd(t('storia.p3', 'Vorremmo condividere la conoscenza maturata nel rispetto dei luoghi e delle persone che ci consentono di beneficiare di tesori naturalistici inestimabili. **Tutto questo è Insolita Sardegna.**'))}</p>
              <div className="hero__cta" style={{ marginTop: 36 }}>
                <Link to="/iscrizioni" className="btn btn-acid">{t('storia.chisiamo.cta', 'Info e iscrizioni')}</Link>
                <a href={`tel:${phone}`} className="btn btn-ghost-ink">(+39) {phone.replace('+39', '')}</a>
              </div>
            </div>
            <div className="split__media reveal" data-d="1">
              <img src={img('storia.split.image', '/img/allenamento-motorally.jpg')} alt="Enduro Insolita Sardegna" />
              <span className="frame" />
              <span className="tag">Est. 2020 — Adventure</span>
            </div>
          </div>
        </div>
      </section>

      <section className="sec sec--ink">
        <div className="wrap">
          <div className="sec-head reveal">
            <div>
              <span className="eyebrow on-dark">{t('storia.valori.eyebrow', 'I nostri valori')}</span>
              <h2 className="h-sec" style={{ marginTop: '.35em' }}>{renderMultiline(t('storia.valori.title', 'Ciò in cui\ncrediamo'))}</h2>
            </div>
          </div>
          <ul className="feat-list">
            <li className="reveal"><span className="t"><b>{t('storia.valori1.title', 'Sport & Avventura')}</b><span>{t('storia.valori1.text', 'Incentivare uno sport che regala libertà, adrenalina e legami autentici.')}</span></span></li>
            <li className="reveal" data-d="1"><span className="t"><b>{t('storia.valori2.title', 'Rispetto del territorio')}</b><span>{t('storia.valori2.text', 'Vivere la natura senza lasciare traccia, valorizzando i luoghi che attraversiamo.')}</span></span></li>
            <li className="reveal" data-d="2"><span className="t"><b>{t('storia.valori3.title', 'Condivisione')}</b><span>{t('storia.valori3.text', 'Trasmettere la conoscenza dei sentieri e la cultura della Sardegna più autentica.')}</span></span></li>
            <li className="reveal" data-d="3"><span className="t"><b>{t('storia.valori4.title', 'Comunità')}</b><span>{t('storia.valori4.text', 'Una famiglia di motociclisti devoti, uniti dalla stessa passione per le due ruote.')}</span></span></li>
          </ul>
        </div>
      </section>

      {/* MOTO CLUB — cosa facciamo */}
      <section className="sec">
        <div className="wrap">
          <div className="sec-head reveal">
            <div>
              <span className="eyebrow">{t('storia.club.eyebrow', 'Il Moto Club')}</span>
              <h2 className="h-sec" style={{ marginTop: '.35em' }}>{t('club.about.title', 'Cosa facciamo')}</h2>
            </div>
          </div>
          <div className="reveal" style={{ maxWidth: '82ch' }}>
            <p className="lead" style={{ whiteSpace: 'pre-line' }}>{t('club.about', CLUB_ABOUT_FALLBACK)}</p>
          </div>
        </div>
      </section>

      <CtaBand image={img('storia.cta.image', '/img/allenamento-2022-insolita-sardegna.jpg')} eyebrow={t('storia.cta.eyebrow', 'Entra a far parte del club')} title={t('storia.cta.title', 'Insolita\nSardegna')}>
        <div className="band__phone"><span>{t('storia.cta.badge', 'Tesseramento 2026 aperto')}</span></div>
        <div style={{ position: 'relative' }}>
          <Link to="/iscrizioni" className="btn btn-acid" style={{ marginTop: 24 }}>{t('storia.cta.button', 'Iscriviti ora')}</Link>
        </div>
      </CtaBand>
    </>
  );
};

export default Storia;
